import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, MessageCircle, Check } from "lucide-react";
import { useCtaVariant, buildWhatsAppLink } from "@/lib/cta-variants";
import { useLanguage } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ───────────────────────── URL helpers ─────────────────────────
function readUTMs() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
  };
}

function detectReferrerFromUtms(u) {
  if (!u || !u.utm_source) return "";
  if (/farm|pharma/i.test(u.utm_source)) return "farmacia";
  if (/insta|tiktok|face|social|ads/i.test(u.utm_source)) return "xarxes";
  return "";
}

function readPharmacyCodeFromUrl() {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return params.get("codi") || params.get("pharmacy_code") || "";
}

// ───────────────────────── Main component ─────────────────────────
export default function WaitlistForm({ variant = "hero", source = "landing_v1", buttonLabel }) {
  const cta = useCtaVariant();
  const { t, lang } = useLanguage();

  // Lead data
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [pharmacyCode, setPharmacyCode] = useState(readPharmacyCodeFromUrl);
  const [utms] = useState(readUTMs);
  const [referrer, setReferrer] = useState(() => detectReferrerFromUtms(readUTMs()));

  // Screening
  const [screeningWalking, setScreeningWalking] = useState("");
  const [screeningChair, setScreeningChair] = useState("");
  const [mainDifficulty, setMainDifficulty] = useState("");

  // Consent + flow state
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // ─── Submission ───
  const postLead = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("form.errors.invalidEmail"));
      return false;
    }
    if (!consent) {
      toast.error(t("form.errors.consentRequired"));
      return false;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        whatsapp: whatsapp || undefined,
        pharmacy_code: pharmacyCode || undefined,
        referrer: referrer || undefined,
        variant: cta.key,
        screening_walking: screeningWalking || undefined,
        screening_chair: screeningChair || undefined,
        main_difficulty: mainDifficulty || undefined,
        source,
        language: lang,
        consent_accepted: true,
        ...utms,
      });
      setSuccess(true);
      toast.success(t("form.errors.success"));
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || t("form.errors.generic");
      toast.error(msg);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); postLead(); };

  // ─── Routing: success → hero variant → multi-step ───
  if (success) {
    return <SuccessState email={email} cta={cta} />;
  }

  if (variant === "hero") {
    return (
      <HeroForm
        email={email}
        onEmailChange={setEmail}
        consent={consent}
        onConsentChange={setConsent}
        submitting={submitting}
        onSubmit={onSubmit}
        cta={cta}
        buttonLabel={buttonLabel}
      />
    );
  }

  return (
    <MultiStepForm
      step={step}
      setStep={setStep}
      // screening
      screeningWalking={screeningWalking}
      setScreeningWalking={setScreeningWalking}
      screeningChair={screeningChair}
      setScreeningChair={setScreeningChair}
      mainDifficulty={mainDifficulty}
      setMainDifficulty={setMainDifficulty}
      // final-details
      email={email}
      setEmail={setEmail}
      whatsapp={whatsapp}
      setWhatsapp={setWhatsapp}
      pharmacyCode={pharmacyCode}
      setPharmacyCode={setPharmacyCode}
      referrer={referrer}
      setReferrer={setReferrer}
      consent={consent}
      setConsent={setConsent}
      // flow
      submitting={submitting}
      onSubmit={onSubmit}
      cta={cta}
    />
  );
}

// ───────────────────────── Success state ─────────────────────────
function SuccessState({ email, cta }) {
  const { t } = useLanguage();
  const waLink = buildWhatsAppLink(email);
  return (
    <div
      data-testid="waitlist-success-message"
      className="border border-[#B4C5A4] bg-[#B4C5A4]/15 rounded-md p-6 max-w-xl"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="h-6 w-6 rounded-full bg-[#0B2545] flex items-center justify-center">
          <Check className="h-3.5 w-3.5 text-[#F9F8F6]" strokeWidth={3} />
        </div>
        <p className="font-sans-display text-sm tracking-[0.18em] uppercase text-[#0B2545]">
          {cta.successTitle}
        </p>
      </div>
      <p className="text-[#0F172A] text-base leading-relaxed mb-4">{cta.successBody}</p>
      {waLink && (
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="success-whatsapp-button"
          className="lift-btn inline-flex items-center gap-2 h-11 px-5 bg-[#25D366] text-white font-sans-display font-medium tracking-wide rounded-md hover:bg-[#1ebe5d]"
        >
          <MessageCircle className="h-4 w-4" strokeWidth={2} />
          {t("form.success.whatsappBtn")}
        </a>
      )}
    </div>
  );
}

// ───────────────────────── Hero inline form ─────────────────────────
function HeroForm({ email, onEmailChange, consent, onConsentChange, submitting, onSubmit, cta, buttonLabel }) {
  const { t } = useLanguage();
  return (
    <form
      onSubmit={onSubmit}
      className="space-y-3 max-w-xl"
      data-testid="hero-waitlist-form"
    >
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder={t("form.hero.emailPlaceholder")}
          data-testid="hero-email-input"
          className="flex-1 h-12 px-4 bg-white border border-slate-200 rounded-md text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B2545]/20 focus:border-[#0B2545] transition"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="hero-submit-button"
          className="lift-btn h-12 px-6 bg-[#0B2545] text-[#F9F8F6] font-sans-display font-medium tracking-wide rounded-md hover:bg-[#0a1f3a] disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {submitting ? t("form.full.submitFallback") : (buttonLabel || cta.heroButton)}
        </button>
      </div>
      <ConsentCheckbox
        checked={consent}
        onChange={onConsentChange}
        testid="hero-consent-checkbox"
        showDetail={false}
      />
    </form>
  );
}

// ───────────────────────── Multi-step form (screening + final) ─────────────────────────
const STEPS = ["walking", "chair", "difficulty", "form"];

function MultiStepForm(props) {
  const { step, setStep, submitting, onSubmit, cta } = props;
  const { t } = useLanguage();

  const screeningOptions = [
    { value: "yes", label: t("form.screening.screeningOptions.yes") },
    { value: "sometimes", label: t("form.screening.screeningOptions.sometimes") },
    { value: "no", label: t("form.screening.screeningOptions.no") },
  ];

  const difficultyOptions = [
    { value: "dolor", label: t("form.screening.difficultyOptions.dolor") },
    { value: "vergonya", label: t("form.screening.difficultyOptions.vergonya") },
    { value: "no_se_per_on", label: t("form.screening.difficultyOptions.no_se_per_on") },
    { value: "constancia", label: t("form.screening.difficultyOptions.constancia") },
    { value: "altres", label: t("form.screening.difficultyOptions.altres") },
  ];

  const totalSteps = STEPS.length;
  const progress = ((step + 1) / totalSteps) * 100;

  const advanceTo = (nextStep) => setTimeout(() => setStep(nextStep), 200);

  return (
    <div data-testid="final-waitlist-form" className="max-w-xl mx-auto">
      <StepHeader
        step={step}
        totalSteps={totalSteps}
        progress={progress}
        onBack={() => setStep(step - 1)}
      />

      {step === 0 && (
        <ScreeningStep
          eyebrow={t("form.screening.walking.eyebrow")}
          question={t("form.screening.walking.question")}
          subtitle={t("form.screening.walking.subtitle")}
          value={props.screeningWalking}
          onSelect={(v) => { props.setScreeningWalking(v); advanceTo(1); }}
          options={screeningOptions}
          testIdPrefix="screening-walking"
        />
      )}

      {step === 1 && (
        <ScreeningStep
          eyebrow={t("form.screening.chair.eyebrow")}
          question={t("form.screening.chair.question")}
          subtitle={t("form.screening.chair.subtitle")}
          value={props.screeningChair}
          onSelect={(v) => { props.setScreeningChair(v); advanceTo(2); }}
          options={screeningOptions}
          testIdPrefix="screening-chair"
        />
      )}

      {step === 2 && (
        <ScreeningStep
          eyebrow={t("form.screening.difficulty.eyebrow")}
          question={t("form.screening.difficulty.question")}
          subtitle={t("form.screening.difficulty.subtitle")}
          value={props.mainDifficulty}
          onSelect={(v) => { props.setMainDifficulty(v); advanceTo(3); }}
          options={difficultyOptions}
          testIdPrefix="screening-difficulty"
        />
      )}

      {step === 3 && (
        <FinalDetailsStep
          email={props.email}
          setEmail={props.setEmail}
          whatsapp={props.whatsapp}
          setWhatsapp={props.setWhatsapp}
          pharmacyCode={props.pharmacyCode}
          setPharmacyCode={props.setPharmacyCode}
          referrer={props.referrer}
          setReferrer={props.setReferrer}
          consent={props.consent}
          setConsent={props.setConsent}
          submitting={submitting}
          onSubmit={onSubmit}
          cta={cta}
        />
      )}
    </div>
  );
}

// ───────────────────────── Step header (progress + back) ─────────────────────────
function StepHeader({ step, totalSteps, progress, onBack }) {
  const { t } = useLanguage();
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.18em] text-[#475569] font-sans-display">
          {t("form.screening.stepLabel")} {step + 1} {t("form.screening.stepOf")} {totalSteps}
        </span>
        {step > 0 && step < totalSteps && (
          <button
            type="button"
            onClick={onBack}
            data-testid="screening-back-button"
            className="text-xs text-[#475569] hover:text-[#0B2545] inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" /> {t("form.screening.back")}
          </button>
        )}
      </div>
      <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#0B2545] transition-all duration-500 rounded-full"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

// ───────────────────────── Final details step (email + whatsapp + farmàcia + referrer) ─────────────────────────
function FinalDetailsStep({
  email, setEmail,
  whatsapp, setWhatsapp,
  pharmacyCode, setPharmacyCode,
  referrer, setReferrer,
  consent, setConsent,
  submitting, onSubmit, cta,
}) {
  const { t } = useLanguage();
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.18em] text-[#475569] font-sans-display mb-2">
          {t("form.full.almostThere")}
        </p>
        <h3 className="font-sans-display text-2xl text-[#0F172A] tracking-tight leading-tight">
          {t("form.full.finalHeading1")}
          <span className="block italic font-serif-display font-light text-[#475569]">
            {t("form.full.finalHeading2")}
          </span>
        </h3>
      </div>

      <div>
        <label htmlFor="final-email" className="block text-xs uppercase tracking-widest text-[#475569] mb-2 font-sans-display">
          {t("form.full.emailLabel")} <span className="text-[#0B2545]">{t("form.full.emailRequired")}</span>
        </label>
        <input id="final-email" type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("form.full.emailPlaceholder")}
          data-testid="final-email-input"
          className="w-full h-12 px-4 bg-white border border-slate-200 rounded-md text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B2545]/20 focus:border-[#0B2545] transition"
        />
      </div>

      <div>
        <label htmlFor="final-whatsapp" className="block text-xs uppercase tracking-widest text-[#475569] mb-2 font-sans-display">
          {t("form.full.whatsappLabel")} <span className="text-slate-400 normal-case tracking-normal">{t("form.full.whatsappOptional")}</span>
        </label>
        <input id="final-whatsapp" type="tel" value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          placeholder={t("form.full.whatsappPlaceholder")}
          data-testid="final-whatsapp-input"
          className="w-full h-12 px-4 bg-white border border-slate-200 rounded-md text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B2545]/20 focus:border-[#0B2545] transition"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="final-pharmacy" className="block text-xs uppercase tracking-widest text-[#475569] mb-2 font-sans-display">
            {t("form.full.pharmacyLabel")} <span className="text-slate-400 normal-case tracking-normal">{t("form.full.pharmacyOptional")}</span>
          </label>
          <input id="final-pharmacy" type="text" value={pharmacyCode}
            onChange={(e) => setPharmacyCode(e.target.value.toUpperCase())}
            placeholder={t("form.full.pharmacyPlaceholder")}
            data-testid="final-pharmacy-input" maxLength={20}
            className="w-full h-12 px-4 bg-white border border-slate-200 rounded-md text-[#0F172A] placeholder:text-slate-400 tracking-wider font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#0B2545]/20 focus:border-[#0B2545] transition"
          />
        </div>
        <div>
          <label htmlFor="final-referrer" className="block text-xs uppercase tracking-widest text-[#475569] mb-2 font-sans-display">
            {t("form.full.referrerLabel")}
          </label>
          <select id="final-referrer" value={referrer}
            onChange={(e) => setReferrer(e.target.value)}
            data-testid="final-referrer-select"
            className="w-full h-12 px-3 bg-white border border-slate-200 rounded-md text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0B2545]/20 focus:border-[#0B2545] transition"
          >
            <option value="">{t("form.full.referrerSelect")}</option>
            <option value="farmacia">{t("form.full.referrerOptions.farmacia")}</option>
            <option value="xarxes">{t("form.full.referrerOptions.xarxes")}</option>
            <option value="amic">{t("form.full.referrerOptions.amic")}</option>
            <option value="cerca">{t("form.full.referrerOptions.cerca")}</option>
            <option value="altre">{t("form.full.referrerOptions.altre")}</option>
          </select>
        </div>
      </div>

      <ConsentCheckbox
        checked={consent}
        onChange={setConsent}
        testid="final-consent-checkbox"
        showDetail={true}
      />

      <button
        type="submit"
        disabled={submitting}
        data-testid="final-submit-button"
        className="lift-btn w-full h-12 bg-[#0B2545] text-[#F9F8F6] font-sans-display font-medium tracking-wide rounded-md hover:bg-[#0a1f3a] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? t("form.full.submitFallback") : cta.finalButton}
      </button>
      <p className="text-xs text-[#475569] text-center">
        {t("form.full.formNote")}
      </p>
    </form>
  );
}

// ───────────────────────── Screening step (single question + options) ─────────────────────────
function ScreeningStep({ eyebrow, question, subtitle, value, onSelect, options, testIdPrefix }) {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[#475569] font-sans-display mb-3">
          {eyebrow}
        </p>
        <h3 className="font-sans-display text-2xl sm:text-3xl text-[#0F172A] tracking-tight leading-tight mb-3">
          {question}
        </h3>
        <p className="text-sm text-[#475569] leading-relaxed">{subtitle}</p>
      </div>
      <div className="space-y-3">
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelect(opt.value)}
              data-testid={`${testIdPrefix}-${opt.value}`}
              className={`w-full text-left flex items-center justify-between gap-4 px-5 py-4 border rounded-lg transition-all ${
                selected
                  ? "border-[#0B2545] bg-[#0B2545]/5 ring-2 ring-[#0B2545]/15"
                  : "border-slate-200 bg-white hover:border-[#0B2545]/40 hover:bg-[#F9F8F6]"
              }`}
            >
              <span className="font-sans-display text-[#0F172A] text-base">{opt.label}</span>
              <ArrowRight
                className={`h-4 w-4 transition-colors ${selected ? "text-[#0B2545]" : "text-slate-400"}`}
                strokeWidth={1.5}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────── Consent checkbox (RGPD) ─────────────────────────
function ConsentCheckbox({ checked, onChange, testid, showDetail }) {
  const { t } = useLanguage();
  return (
    <div className="flex items-start gap-3 pt-1">
      <input
        id={testid}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="mt-1 h-4 w-4 accent-[#0B2545] cursor-pointer flex-shrink-0"
      />
      <label htmlFor={testid} className="text-xs text-[#475569] leading-relaxed cursor-pointer">
        {t("form.consent.preLink")}{" "}
        <Link
          to="/politica-privacitat"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0B2545] underline underline-offset-2 hover:text-[#0a1f3a]"
          data-testid={`${testid}-policy-link`}
        >
          {t("form.consent.link")}
        </Link>
        {t("form.consent.postLink")}
        {showDetail && (
          <span className="block mt-1 text-[11px] text-slate-400 leading-relaxed">
            {t("form.consent.detail")}
          </span>
        )}
      </label>
    </div>
  );
}
